import { io } from "socket.io-client";
import { getNormalizedStatusCode } from "utils/get-normalized-status-code";
import { getSide } from "utils/get-side";
import { getXY } from "utils/getXY";
import { SIDE } from "_constants";
import { DEV_TRACKER_API, trackerApiForHost } from "utils/tracker-api-base";

const isBrowser = typeof window !== "undefined";

const BASE_URL =
  process.env.REACT_APP_TRACKER_API ||
  (isBrowser ? trackerApiForHost(window.location.hostname) : DEV_TRACKER_API);

const getRealReferrer = () => {
  if (!isBrowser) return "";
  return document.referrer || window.location.origin;
};

export const getStatus = async (eventId: string) => {
  const response = await (
    await fetch(`${BASE_URL}/check/${eventId}`, {
      headers: {
        "X-Real-Referrer": getRealReferrer(),
      },
    })
  ).json();
  const [x, y] = getXY(response);
  return {
    ...response,
    nsc: getNormalizedStatusCode(response),
    side: getSide(response),
    x,
    y,
  } as MatchStatus;
};

export const getSocketConnection = (eventId: string) => {
  return io(`${BASE_URL}?event=${eventId}`, {
    forceNew: true,
    reconnectionAttempts: 9999,
    timeout: 10000,
    transports: ["websocket"],
  });
};

export const getMatchDictionary = async (sport?: SPORT) => {
  const url = sport ? `${BASE_URL}/list?sport=${sport}` : `${BASE_URL}/list`;
  const response = await (
    await fetch(url, {
      headers: {
        "X-Real-Referrer": getRealReferrer(),
      },
    })
  ).json();
  return response as { body: Record<SPORT, MatchStatus[]> };
};

const stripTrailingSlash = (value?: string) =>
  value ? value.replace(/\/+$/, "") : value;

const bufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const { length } = bytes;
  for (let index = 0; index < length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
};

const getThemeApiBases = (): string[] => {
  const bases: string[] = [];
  const normalizedBase = stripTrailingSlash(BASE_URL);
  if (normalizedBase) {
    bases.push(normalizedBase);
  }

  if (isBrowser && window.location.origin) {
    const origin = stripTrailingSlash(window.location.origin);
    if (origin) {
      if (!normalizedBase) {
        bases.push(origin);
      } else {
        try {
          const originHost = new URL(origin).host;
          const baseHost = new URL(normalizedBase).host;
          if (originHost === baseHost && !bases.includes(origin)) {
            bases.push(origin);
          }
        } catch {
          // ignore URL parsing errors
        }
      }
    }
  }

  if (!bases.length) {
    if (normalizedBase) {
      bases.push(normalizedBase);
    } else if (isBrowser && window.location.origin) {
      const fallbackOrigin = stripTrailingSlash(window.location.origin);
      if (fallbackOrigin) {
        bases.push(fallbackOrigin);
      }
    }
  }

  return bases;
};

const stripHtmlTags = (content: string) =>
  content
    .replace(/<\/?[^>]+(>|$)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractErrorMessage = (payload: string | null) => {
  if (!payload) return "";
  try {
    const parsed = JSON.parse(payload);
    if (parsed && typeof parsed === "object") {
      return (
        parsed.error ||
        parsed.message ||
        (typeof parsed.detail === "string" ? parsed.detail : "")
      );
    }
  } catch {
    // ignore JSON parse errors
  }
  if (payload.startsWith("<")) {
    const sanitized = stripHtmlTags(payload);
    if (sanitized) {
      return sanitized;
    }
  }
  return payload;
};

const requestThemeEndpoint = async (path: string, init?: RequestInit) => {
  const bases = getThemeApiBases();
  let lastError: Error | null = null;

  for (let index = 0; index < bases.length; index += 1) {
    const base = bases[index];
    const url = `${base}${path}`;
    try {
      const response = await fetch(url, init);
      if (response.ok) {
        return response;
      }
      const errorPayload = await response.text().catch(() => null);
      const message =
        extractErrorMessage(errorPayload) ||
        `Theme request failed with status ${response.status}`;
      if (response.status === 404 && index < bases.length - 1) {
        lastError = new Error(message);
        continue;
      }
      throw new Error(message);
    } catch (error) {
      lastError =
        error instanceof Error ? error : new Error(String(error));
      if (index < bases.length - 1) {
        continue;
      }
    }
  }

  throw lastError ?? new Error("Theme request failed");
};

export type ThemeConfigResponse = {
  customer: string;
  resolvedCustomer: string;
  source: "default" | "existing";
  exists: boolean;
  config: Record<string, unknown>;
};

export type ThemeSaveResponse = {
  customer: string;
  overwritten: boolean;
  path: string;
};

export type ThemeAssetUploadResponse = {
  path: string;
};

const buildThemeConfigUrl = (customer?: string) => {
  const query = customer ? `?customer=${encodeURIComponent(customer)}` : "";
  return `/theme-config${query}`;
};

export const fetchThemeConfig = async (
  customer?: string
): Promise<ThemeConfigResponse> => {
  const response = await requestThemeEndpoint(
    buildThemeConfigUrl(customer),
    {
      headers: {
        "X-Real-Referrer": getRealReferrer(),
      },
    }
  );
  return (await response.json()) as ThemeConfigResponse;
};

export const saveThemeConfig = async (
  customer: string,
  config: Record<string, unknown>
): Promise<ThemeSaveResponse> => {
  const response = await requestThemeEndpoint(buildThemeConfigUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Real-Referrer": getRealReferrer(),
    },
    body: JSON.stringify({ customer, config }),
  });
  return (await response.json()) as ThemeSaveResponse;
};

export const uploadThemeAsset = async (
  customer: string,
  file: File
): Promise<ThemeAssetUploadResponse> => {
  const fileBuffer = await file.arrayBuffer();
  const payload = {
    customer,
    fileName: file.name,
    content: bufferToBase64(fileBuffer),
  };
  const response = await requestThemeEndpoint("/theme-assets/upload", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Real-Referrer": getRealReferrer(),
    },
    body: JSON.stringify(payload),
  });
  return (await response.json()) as ThemeAssetUploadResponse;
};

export enum SPORT {
  BASKETBALL = 18,
  VOLLEYBALL = 91,
  BASEBALL = 7,
  ICE_HOCKEY = 17,
  FOOTBALL = 1,
  TENNIS = 13,
}

export type TeamKit = {
  id: number;
  si: string; // comma-separated hex colors
  so?: string; // socks or outline color
};

export type TeamNode = {
  n?: string; // team name
  kit?: TeamKit; // optional kit block
};

export type MatchStatus<TStatus = string> = {
  id?: string;
  sts: string;
  s: SPORT;
  tm: number;
  T1?: TeamNode;
  T2?: TeamNode;
  t1?: TeamNode;
  t2?: TeamNode;
  T1n?: string;
  T2n?: string;
  T1c?: string;
  T2c?: string;
  c: Record<string, any>;
  pc: number;
  sc?: TStatus;
  nsc?: string;
  stn?: string;
  xy?: string;
  side?: SIDE;
  hidePath?: boolean;
  x?: number;
  y?: number;
  ta?: string;
};
